import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { CallState } from '@entities/call-item';

import type { CallsState, HistoricalCall, MediaState } from './types';

/* -------------------------------------------------------------------------- */
/*                               STATE SHAPE                                  */
/* -------------------------------------------------------------------------- */

/**
 * 1. list — плоский массив карточек (чтобы легко рендерить «справа»)
 * 2. currentId — id звонка, который отображается в основном окне (null, если нет)
 *
 * Всё. Состояния вроде «hold»/«active»/«ended» — это просто поле `state` у самой
 * карточки. Логику переключения (когда какой‑то звонок уходит в hold, другой
 * становится current и т. д.) мы реализуем во внешнем обработчике, диспатча
 * нужные экшены в правильном порядке.
 */
const initialState: CallsState = {
	list: [],
	currentId: null,
};

const callsSlice = createSlice({
	name: 'calls',
	initialState,
	reducers: {
		/** Добавить элемент в историю */
		addCall(state, action: PayloadAction<HistoricalCall>) {
			state.list.unshift(action.payload);
		},

		/** Частичное обновление (патч) */
		patchCall(state, action: PayloadAction<{ id: string; changes: Partial<HistoricalCall> }>) {
			const call = state.list.find((c) => c.id === action.payload.id);
			if (call) Object.assign(call, action.payload.changes);
		},

		/** Удалить звонок */
		removeCall(state, action: PayloadAction<string>) {
			state.list = state.list.filter((c) => c.id !== action.payload);
			if (state.currentId === action.payload) state.currentId = null;
		},

		/** Очистить историю полностью */
		clearCalls(state) {
			state.list = [];
			state.currentId = null;
		},

		/** Установить/обновить активный звонок */
		setCurrentCall(state, action: PayloadAction<string | null>) {
			state.currentId = action.payload;
		},

		/** Сменить state звонка (active / hold / ended / declined / …) */
		setCallState(state, action: PayloadAction<{ id: string; state: CallState }>) {
			const call = state.list.find((c) => c.id === action.payload.id);
			if (call) call.state = action.payload.state;
		},

		/** Медиаблок (mute, unmute, pause видео и т.п.) */
		updateMedia(
			state,
			action: PayloadAction<{
				id: string;
				target: 'local' | 'remote';
				changes: Partial<MediaState>;
			}>
		) {
			const call = state.list.find((c) => c.id === action.payload.id);
			if (call) {
				call[action.payload.target] = {
					...call[action.payload.target],
					...action.payload.changes,
				} as MediaState;
				state.list = [...state.list]; // то же самое
			}
		},

		/** Статус демонстрации экрана */
		setScreenShare(state, action: PayloadAction<{ id: string; active: boolean }>) {
			const call = state.list.find((c) => c.id === action.payload.id);
			if (call) call.screenShare = action.payload.active;
		},
	},
});

export const {
	addCall,
	patchCall,
	removeCall,
	clearCalls,
	setCurrentCall,
	setCallState,
	updateMedia,
	setScreenShare,
} = callsSlice.actions;

export const callsReducer = callsSlice.reducer;
